export type Pt = {x: number; y: number};
export type PathMeshSet = Pt[][];

/** Discriminated union of rasterizable terrain features: produced by `collectRasterFeatures`
 *  from `MapObject_ForTerrainSystem` inputs; tests construct them directly to exercise the
 *  rasterizer without touching the data-model layer. */
export type RasterFeature =
	| ClosedRingFeature
	| OpenCurveFeature
	| CliffFeature
	| SpotHeightFeature;

/** Shared solver/BC fields of the two pin-carrying path features (`ClosedRingFeature` +
 *  `OpenCurveFeature`). Geometry, elevation model, and Neumann face-count stay per-kind —
 *  `crossingSlope` is a shared scalar, resolved from `PathTerrainFeature.crossingSlope` (Interval). */
export interface PathFeatureBase {
	/** World-space tessellated polyline. */
	polyline: Pt[];
	/** Target solver cell size (m) along this feature. Undefined → config default. */
	cellSize?: number;
	/** If true, the feature's cells become Barrier (no-flux) instead of Dirichlet/SoftPin.
	 *  Ring: rim cells are Barrier (walled plateau / barrier ridge). Curve: polyline cells
	 *  are pure Barrier — elevation ignored; the bridge drops `softPin*` when `noFlux`. */
	noFlux?: boolean;
	/** **2026-07-30/31 — `pinType: "soft"`.**  True → pin cells are SoftPin (not Dirichlet):
	 *  Laplace stencil PLUS penalty `k·(H − target)` toward the resolved elevation; k=`softPinStiffness`. */
	softPin?: boolean;
	/** User-facing stiffness k for the soft-pin penalty (only meaningful when `softPin:true`).
	 *  Rasterizer multiplies by 1/Δ² before storing in `cells.softPinStiffness` so k=1
	 *  ≈ one Laplacian neighbour's contribution at any cell size. Default 1.0. */
	softPinStiffness?: number;
	/** **2026-07-30/31 — `softPinBandWidth` (m).**  >0 → spread the soft-pin penalty across a
	 *  band of width W; stiffness ramps `cos(π·d/(2W))` (1 at pin → 0 at edge) → C¹-ish, no seam.
	 *  Pull grows with W — lower `softPinStiffness` when widening. Default 0 = 1-cell. See Set7 doc. */
	softPinBandWidth?: number;
	/** **2026-08-02 — pin dominance.**  Larger value wins: a Dirichlet/SoftPin write to a
	 *  cell already pinned earlier is blocked iff the existing cell's dominance is STRICTLY
	 *  greater; equal/lower = no block (later-writer-wins). Default 0. See `PathTerrainFeature.dominance`. */
	dominance?: number;
	/** **2026-08-02 — fallback pin.**  When true and this feature's Dirichlet/SoftPin write is
	 *  blocked by a higher-dominance cell, reroute the pin to the closest leaf inside the
	 *  feature's own footprint whose dominance isn't higher than ours. Default false. See `PathTerrainFeature.fallbackPin`. */
	fallbackPin?: boolean;
	/** Resolved crossingSlope (m/m) for the Neumann faces; undefined → no Neumann.
	 *  Ring: single dominant-outward face, `g0Signed = interiorUp ? −crossingSlope : +crossingSlope`.
	 *  Curve: symmetric `+g0` on both side-faces. Phase 3 (§12.13/12.14) checks the ring case. */
	crossingSlope?: number;
}

export interface ClosedRingFeature extends PathFeatureBase {
	kind: "closedRing";
	/** Resolved pinned elevation for the pinned cells. */
	elevation: number;
	/** Per-tessellated-point resolved rim-cell elevations (varies along the rim when
	 *  `perVertexElevations` has non-null entries). Length === polyline.length; undefined
	 *  → use `elevation` for every rim cell (constant-elevation closedIsoline case). */
	rimElevations?: number[];
	/** Per-region diffusion-coefficient override for the pinned cells. Default 1. */
	regionC?: number;
	/** Per-region Poisson source override for the pinned cells. Default 0. */
	regionF?: number;
	/** Which side is uphill. Default "interiorUp". */
	orientation?: "interiorUp" | "interiorDown";
	/** **2026-07-28 decomposition (replaces closedIsoline role):** true (default) pins interior
	 *  + rim at `elevation`; false pins ONLY rim cells — interior solves freely, enabling a
	 *  mountainside between this rim and inner peaks (former `openDirichlet` on a closed ridge). */
	pinInterior: boolean;
	/** **2026-07-31 — band BFS direction** (only meaningful when `softPin:true` +
	 *  `softPinBandWidth > 0`). Bridge sets from `pinShape`: `"both"` ramps both sides (rim+soft);
	 *  `"outward"` ramps only the exterior edge (interior+soft keeps a uniform-k plateau). */
	softPinBandDirection?: "both" | "outward";
	/** **2026-08-03 — opt-in mute of this ring's `dominance` for the working solve based on
	 *  the camera viewport vs this ring's polyline; folded into `rasterFeatureSignature` so a
	 *  change re-solves. Not consumed by the rasterizer — see `PathTerrainFeature.muteDominanceWhen`. */
	muteDominanceWhen?: "paddedViewportFullyInside" | "paddedViewportFullyOutside";
	/** **2026-08-05 — ambient-noise application multiplier for the region INSIDE this ring's polygon.** Forwarded from `PathTerrainFeature.ambientWeightMultiplier`; consumed only at eval time by the Elevation sampler (see `evalElevationTwoGrid` — applied to the `L + D·w` ambient term, NOT the Poisson solve). The innermost containing ring wins for a point. NOT folded into `rasterFeatureSignature` (the multiplier does not affect the macro/working solves — changing it must NOT re-solve the grid). Default `undefined` = 1 (full ambient — no change to existing maps). */
	ambientWeightMultiplier?: number;
}

export interface OpenCurveFeature extends PathFeatureBase {
	kind: "openCurve";
	/** Elevation at each tessellated point (length === polyline.length).
	 *  Caller interpolates per-vertex elevations along the tessellation. */
	elevations: number[];
}

export interface CliffFeature {
	kind: "cliff";
	/** World-space tessellated open polyline along the cliff line. */
	polyline: Pt[];
	/** Target solver cell size (m). Undefined → config default. */
	cellSize?: number;
	/** If true → full Barrier (no-flux). If false → steep-but-continuous (Free, just refined). */
	noFlux: boolean;
}

export interface SpotHeightFeature {
	kind: "spotHeight";
	/** Anchor position (world meters). */
	x: number; y: number;
	/** Pinned elevation. */
	elevation: number;
	/** Pin radius (m): cells within this radius are pinned at `elevation`. */
	radius: number;
	/** Target solver cell size (m) for the pin disk. Undefined → config default. */
	cellSize?: number;
	/** **2026-08-02 — pin dominance.**  See `ClosedRingFeature.dominance`. */
	dominance?: number;
	/** **2026-08-02 — fallback pin.**  Search scope = within this spot's disk radius.
	 *  See `PathTerrainFeature.fallbackPin`. */
	fallbackPin?: boolean;
}

// ============================================================
// Feature signatures (auto-regen gate — see docblock below)
// ============================================================

/** Stable, content-addressed signature for one `RasterFeature` — folds every solver-relevant field incl. the
 *  full tessellated arrays. **2026-07-29 regression:** gates hashed only counts+scalars, so shape-only edits
 *  missed re-solve (repro: curveType basic→fbmNoise). All gates now fold coordinates via this shared helper. */
export function rasterFeatureSignature(f: RasterFeature): string {
	switch (f.kind) {
		case "closedRing": {
			// Fold the polyline's coordinates (the tessellated output — catches curveType /
			// curve_fbmNoise_config / worldSeed / perVertexElevations / curve_closed edits),
			// the rim-elevation array (per-vertex rim heights), and every scalar solver field.
			let poly = "";
			for (let i = 0; i < f.polyline.length; i++) {
				poly += `${f.polyline[i].x.toFixed(6)},${f.polyline[i].y.toFixed(6)};`;
			}
			let rim = "";
			if (f.rimElevations != null) {
				for (let i = 0; i < f.rimElevations.length; i++) rim += `${f.rimElevations[i].toFixed(6)};`;
			}
			return `R(e=${f.elevation},pi=${f.pinInterior ? 1 : 0},nf=${f.noFlux ? 1 : 0},cs=${f.cellSize ?? "?"},c=${f.regionC ?? "?"},f=${f.regionF ?? "?"},xs=${f.crossingSlope ?? "?"},o=${f.orientation ?? "?"},sp=${f.softPin ? 1 : 0},spk=${f.softPinStiffness ?? "?"},spw=${f.softPinBandWidth ?? "?"},spdir=${f.softPinBandDirection ?? "?"},dom=${f.dominance ?? "?"},fb=${f.fallbackPin ? 1 : 0},mdw=${f.muteDominanceWhen ?? "-"},rim=[${rim}],poly=[${poly}])`;
		}
		case "openCurve": {
			// Fold the polyline coordinates (the tessellated output) and the per-vertex
			// elevation array (every tessellated point's pinned elevation) — catches the
			// same curveType/fBm/seed/per-vertex class of edits for open features.
			let poly = "";
			for (let i = 0; i < f.polyline.length; i++) {
				poly += `${f.polyline[i].x.toFixed(6)},${f.polyline[i].y.toFixed(6)};`;
			}
			let elev = "";
			for (let i = 0; i < f.elevations.length; i++) elev += `${f.elevations[i].toFixed(6)};`;
			return `O(cs=${f.cellSize ?? "?"},nf=${f.noFlux ? 1 : 0},xs=${f.crossingSlope ?? "?"},elev=[${elev}],dom=${f.dominance ?? "?"},fb=${f.fallbackPin ? 1 : 0},sp=${f.softPin ? 1 : 0},spk=${f.softPinStiffness ?? "?"},spw=${f.softPinBandWidth ?? "?"},poly=[${poly}])`;
		}
		case "cliff": {
			let poly = "";
			for (let i = 0; i < f.polyline.length; i++) {
				poly += `${f.polyline[i].x.toFixed(6)},${f.polyline[i].y.toFixed(6)};`;
			}
			return `C(nf=${f.noFlux ? 1 : 0},cs=${f.cellSize ?? "?"},poly=[${poly}])`;
		}
		case "spotHeight":
			return `S(x=${f.x},y=${f.y},e=${f.elevation},r=${f.radius},cs=${f.cellSize ?? "?"},dom=${f.dominance ?? "?"},fb=${f.fallbackPin ? 1 : 0})`;
	}
}

/** Array-level signature: joins {@link rasterFeatureSignature} over the features in order — order-sensitive
 *  (bridge emits in stable order: object → path → nesting-depth-sorted rings), so identical feature sets
 *  produce the same string; any reordering / addition / removal changes it. */
export function rasterFeaturesSignature(features: ReadonlyArray<RasterFeature>): string {
	const parts: string[] = [];
	for (const f of features) parts.push(rasterFeatureSignature(f));
	return parts.join("|");
}